/**
 * GridCrash — HUD (Heads-Up Display)
 *
 * Loop 8 QOL changes:
 * - Status line now shows difficulty label: "Easy · Flag mines"
 * - Mine stat shows "flags/total" format: "3/10" instead of just "7"
 * - MINES label updated to FLAGS to match new display format
 * - Score cleanly resets to "—" on new game with no flash artifact
 * - New game button resets score tween state so it never shows stale value
 */

import type { GameState, DifficultyConfig } from "../game/engine.js";
import { DIFFICULTIES } from "../game/engine.js";
import { formatTime } from "../game/leaderboard.js";
import { isMuted, toggleMute } from "../game/sound.js";
import { getCompletedCount, getClaimableCount, getStreak } from "../game/quests.js";

// ─── Theme helpers (owned by HUD so the toggle lives next to No-Guess) ────────

const THEME_KEY = "gridcrash.theme";
type Theme = "dark" | "light";

function getStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark") return v;
  } catch { /* private browsing */ }
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  try { localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
}

// Initialise theme immediately on module load (before DOM renders)
applyTheme(getStoredTheme());

// ─── Types ────────────────────────────────────────────────────────────────────

interface HUDCallbacks {
  onNewGame: (difficulty: DifficultyConfig) => void;
  onModeChange: (mode: "random" | "deterministic") => void;
  onDailyChallenge: () => void;
  onVersus: () => void;
  onQuests?: () => void;
  onHint?: () => void;
  onCampaign?: () => void;
  onAftershock?: () => void;
  onGauntlet?: () => void;
}

// ─── HUD Class ────────────────────────────────────────────────────────────────

export class HUD {
  private callbacks: HUDCallbacks;

  // DOM refs
  private timerEl!: HTMLElement;
  private scoreEl!: HTMLElement;
  private minesEl!: HTMLElement;
  private statusEl!: HTMLElement;
  private newGameBtn!: HTMLButtonElement;
  private diffButtons: Map<string, HTMLButtonElement> = new Map();
  private modeToggle!: HTMLInputElement;
  private themeToggleBtn!: HTMLButtonElement;
  private muteToggleBtn!: HTMLButtonElement;
  private questsBtn!: HTMLButtonElement;
  private hintBtn!: HTMLButtonElement;

  // Tween state
  private displayedScore = 0;
  private targetScore    = 0;
  private tweenHandle: ReturnType<typeof requestAnimationFrame> | null = null;
  private tweenStart     = 0;
  private tweenDuration  = 300; // ms

  // Timer color thresholds
  private readonly YELLOW_THRESHOLD = 60;  // 1 min → amber
  private readonly RED_THRESHOLD    = 180; // 3 min → red

  // Track current difficulty and total mines for flag display
  private currentDifficulty!: DifficultyConfig;

  // Versus opponent panel (created lazily, lives above the stats row)
  private opponentPanel: HTMLElement | null = null;
  private statsRowRef!: HTMLElement;

  // Aftershock tremor countdown stat (created lazily, 6C)
  private tremorBlock: HTMLElement | null = null;
  private tremorEl: HTMLElement | null = null;

  // Gauntlet floor + pot stat (created lazily, 6D)
  private gauntletBlock: HTMLElement | null = null;
  private gauntletEl: HTMLElement | null = null;

  constructor(container: HTMLElement, callbacks: HUDCallbacks) {
    this.callbacks = callbacks;
    this.currentDifficulty = Object.values(DIFFICULTIES)[0];
    this.render(container);
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  update(state: Readonly<GameState>, _minesRemaining: number): void {
    this.currentDifficulty = state.difficulty;
    this.updateTimer(state.timeElapsed, state.phase, state.difficulty.timeLimitSecs);
    this.updateScore(state.score, state.phase);
    this.updateMines(state.flagsPlaced, state.difficulty.mineCount);
    this.updateStatus(state.phase, state.difficulty.label);
    this.updateDiffButtons(state.difficulty.id);
    this.updateNewGameBtn(state.phase);
  }

  /** Repaints the quests chip — call after every finished game or claim. */
  refreshQuestChip(): void {
    const done = getCompletedCount();
    const claimable = getClaimableCount();
    const streak = getStreak().count;
    this.questsBtn.textContent =
      `🎯 ${done}/3${streak > 0 ? ` · 🔥${streak}` : ""}`;
    this.questsBtn.classList.toggle("hud__quests-btn--claimable", claimable > 0);
  }

  /**
   * Show/refresh the hint lifeline button. `visible` is false in
   * competitive modes (daily, versus); `usable` gates on phase PLAY
   * and an affordable cost.
   */
  updateHintButton(visible: boolean, cost: number, usable: boolean): void {
    if (!visible) {
      this.hintBtn.style.display = "none";
      return;
    }
    this.hintBtn.style.display = "";
    this.hintBtn.textContent = `💡 ${cost}`;
    this.hintBtn.disabled = !usable;
  }

  /**
   * Aftershock (6C): show reveals-until-tremor as a fourth stat block.
   * Pass null to hide (non-aftershock games).
   */
  updateTremor(countdown: number | null): void {
    if (countdown === null) {
      if (this.tremorBlock) this.tremorBlock.style.display = "none";
      return;
    }
    if (!this.tremorBlock) {
      this.tremorBlock = this.createStatBlock("🌋", "—", "tremor-countdown", "TREMOR");
      this.tremorEl = this.tremorBlock.querySelector(".hud__stat-value");
      this.statsRowRef.appendChild(this.tremorBlock);
    }
    this.tremorBlock.style.display = "";
    if (this.tremorEl) {
      this.tremorEl.textContent = String(countdown);
      this.tremorEl.classList.toggle("hud__stat-value--red", countdown <= 2);
    }
  }

  /**
   * Gauntlet (6D): show "F3 · 240" (floor · run pot) as a stat block.
   * Pass null to hide (non-gauntlet games).
   */
  updateGauntlet(floor: number | null, pot?: number): void {
    if (floor === null) {
      if (this.gauntletBlock) this.gauntletBlock.style.display = "none";
      return;
    }
    if (!this.gauntletBlock) {
      this.gauntletBlock = this.createStatBlock("⛏️", "—", "gauntlet-run", "RUN POT");
      this.gauntletEl = this.gauntletBlock.querySelector(".hud__stat-value");
      this.statsRowRef.appendChild(this.gauntletBlock);
    }
    this.gauntletBlock.style.display = "";
    if (this.gauntletEl) {
      this.gauntletEl.textContent = `F${floor} · ${pot ?? 0}`;
    }
  }

  // ─── Versus opponent panel ────────────────────────────────────────────────────

  /** Show the live opponent panel for a versus match. Call at match start. */
  showOpponentPanel(label: string): void {
    if (!this.opponentPanel) {
      const panel = document.createElement("div");
      panel.className = "hud__opponent";
      panel.innerHTML = `
        <div class="hud__opponent-head">
          <span class="hud__opponent-name">⚔️ <span class="hud__opponent-label"></span></span>
          <span class="hud__opponent-score">0 pts</span>
        </div>
        <div class="hud__opponent-bar-track">
          <div class="hud__opponent-bar-fill" style="width:0%"></div>
        </div>
        <div class="hud__opponent-status">Racing…</div>
      `;
      this.opponentPanel = panel;
      this.statsRowRef.parentElement?.insertBefore(panel, this.statsRowRef);
    }
    this.opponentPanel.querySelector(".hud__opponent-label")!.textContent = label;
    this.opponentPanel.style.display = "";
  }

  /** Update the opponent panel during a match. */
  updateOpponentPanel(score: number, progress: number, alive: boolean, finished: boolean): void {
    if (!this.opponentPanel) return;
    this.opponentPanel.querySelector(".hud__opponent-score")!.textContent = `${score} pts`;
    const fill = this.opponentPanel.querySelector<HTMLElement>(".hud__opponent-bar-fill")!;
    fill.style.width = `${Math.round(progress * 100)}%`;
    const status = this.opponentPanel.querySelector(".hud__opponent-status")!;
    if (finished)      { status.textContent = "✅ Cleared their board"; fill.classList.add("hud__opponent-bar-fill--done"); }
    else if (!alive)   { status.textContent = "💥 Detonated"; fill.classList.add("hud__opponent-bar-fill--dead"); }
    else               { status.textContent = "Racing…"; }
  }

  /** Hide the opponent panel (e.g. returning to solo play). */
  hideOpponentPanel(): void {
    if (this.opponentPanel) this.opponentPanel.style.display = "none";
  }

  // ─── DOM Construction ────────────────────────────────────────────────────────

  private render(container: HTMLElement): void {
    container.innerHTML = "";
    container.className = "hud";

    // ── Row 1: Difficulty Selector ──
    const diffRow = document.createElement("div");
    diffRow.className = "hud__diff-row";
    diffRow.setAttribute("role", "group");
    diffRow.setAttribute("aria-label", "Select difficulty");

    Object.values(DIFFICULTIES).forEach((d) => {
      const btn = document.createElement("button");
      btn.className = "hud__diff-btn";
      btn.dataset["id"] = d.id;
      btn.textContent = d.label;
      btn.setAttribute("aria-pressed", "false");
      btn.title = `${d.rows}×${d.cols} grid, ${d.mineCount} mines`;
      btn.addEventListener("click", () => this.callbacks.onNewGame(d));
      diffRow.appendChild(btn);
      this.diffButtons.set(d.id, btn);
    });

    // ── Row 2: Stats ──
    const statsRow = document.createElement("div");
    statsRow.className = "hud__stats-row";
    this.statsRowRef = statsRow;

    // Flag counter — shows "placed/total" e.g. "3/10"
    const minesBlock = this.createStatBlock("🚩", "0/0", "mines-remaining", "FLAGS");
    this.minesEl = minesBlock.querySelector(".hud__stat-value")!;

    // Timer
    const timerBlock = this.createStatBlock("⏱", "00:00", "elapsed-time", "TIME");
    this.timerEl = timerBlock.querySelector(".hud__stat-value")!;

    // Score
    const scoreBlock = this.createStatBlock("⚡", "—", "current-score", "SCORE");
    this.scoreEl = scoreBlock.querySelector(".hud__stat-value")!;

    statsRow.appendChild(minesBlock);
    statsRow.appendChild(timerBlock);
    statsRow.appendChild(scoreBlock);

    // ── Row 3: Status + New Game + Mode ──
    const controlRow = document.createElement("div");
    controlRow.className = "hud__control-row";

    // Status message
    this.statusEl = document.createElement("div");
    this.statusEl.className = "hud__status";
    this.statusEl.setAttribute("role", "status");
    this.statusEl.setAttribute("aria-live", "polite");
    this.statusEl.textContent = "Tap any cell to start";

    // Daily challenge button
    const dailyBtn = document.createElement("button");
    dailyBtn.className = "hud__daily-btn";
    dailyBtn.textContent = "📅 Daily";
    dailyBtn.setAttribute("aria-label", "Open daily challenge");
    dailyBtn.title = "Daily Challenge — same board for all players today";
    dailyBtn.addEventListener("click", () => this.callbacks.onDailyChallenge());

    // Campaign map button (6B)
    const campaignBtn = document.createElement("button");
    campaignBtn.className = "hud__campaign-btn";
    campaignBtn.textContent = "⛰️ Climb";
    campaignBtn.setAttribute("aria-label", "Open the campaign level map");
    campaignBtn.title = "The Climb — 30 crafted levels, collect the stars";
    campaignBtn.addEventListener("click", () => this.callbacks.onCampaign?.());

    // Quests chip — completed count + streak, refreshed after every game
    this.questsBtn = document.createElement("button");
    this.questsBtn.className = "hud__quests-btn";
    this.questsBtn.setAttribute("aria-label", "Open daily quests");
    this.questsBtn.title = "Daily Quests — bonus coins, new goals every day";
    this.questsBtn.addEventListener("click", () => this.callbacks.onQuests?.());
    this.refreshQuestChip();

    // Versus (CPU) button
    const versusBtn = document.createElement("button");
    versusBtn.className = "hud__versus-btn";
    versusBtn.textContent = "⚔️ Versus";
    versusBtn.setAttribute("aria-label", "Play a competitive match");
    versusBtn.title = "Versus — race an opponent for the higher score";
    versusBtn.addEventListener("click", () => this.callbacks.onVersus());

    // Hint lifeline — hidden in competitive modes, label managed by updateHintButton
    this.hintBtn = document.createElement("button");
    this.hintBtn.className = "hud__hint-btn";
    this.hintBtn.setAttribute("aria-label", "Reveal a safe cell for Clash Coins");
    this.hintBtn.title = "Hint — spend Clash Coins to reveal a guaranteed safe cell";
    this.hintBtn.style.display = "none";
    this.hintBtn.addEventListener("click", () => this.callbacks.onHint?.());

    // Gauntlet mode button (6D)
    const gauntletBtn = document.createElement("button");
    gauntletBtn.className = "hud__gauntlet-btn";
    gauntletBtn.textContent = "⛏️ Gauntlet";
    gauntletBtn.setAttribute("aria-label", "Start a gauntlet run");
    gauntletBtn.title = "The Gauntlet — one life, deeper floors, bank or bust";
    gauntletBtn.addEventListener("click", () => this.callbacks.onGauntlet?.());

    // Aftershock mode button (6C)
    const aftershockBtn = document.createElement("button");
    aftershockBtn.className = "hud__aftershock-btn";
    aftershockBtn.textContent = "🌋 Shock";
    aftershockBtn.setAttribute("aria-label", "Play aftershock mode");
    aftershockBtn.title = "Aftershock — the mines migrate every few reveals";
    aftershockBtn.addEventListener("click", () => this.callbacks.onAftershock?.());

    // New game button
    this.newGameBtn = document.createElement("button");
    this.newGameBtn.className = "hud__new-game-btn";
    this.newGameBtn.textContent = "New Game";
    this.newGameBtn.setAttribute("aria-label", "Start a new game");
    this.newGameBtn.addEventListener("click", () =>
      this.callbacks.onNewGame(this.currentDifficulty)
    );

    // Generation mode toggle
    const modeLabel = document.createElement("label");
    modeLabel.className = "hud__mode-toggle";
    modeLabel.title = "Solvable boards — no 50/50 guessing required";

    this.modeToggle = document.createElement("input");
    this.modeToggle.type = "checkbox";
    this.modeToggle.className = "hud__mode-checkbox";
    this.modeToggle.setAttribute("aria-label", "Enable no-guess mode");
    this.modeToggle.addEventListener("change", () => {
      this.callbacks.onModeChange(
        this.modeToggle.checked ? "deterministic" : "random"
      );
    });

    const modeText = document.createElement("span");
    modeText.className = "hud__mode-label";
    modeText.textContent = "No-Guess";

    modeLabel.appendChild(this.modeToggle);
    modeLabel.appendChild(modeText);

    const controlLeft = document.createElement("div");
    controlLeft.className = "hud__control-left";
    controlLeft.appendChild(this.statusEl);

    // ── Theme toggle ──
    this.themeToggleBtn = document.createElement("button");
    this.themeToggleBtn.className = "hud__theme-toggle";
    this.themeToggleBtn.setAttribute("aria-label", "Toggle dark/light mode");
    this.themeToggleBtn.title = "Toggle dark/light mode";
    this.updateThemeIcon();
    this.themeToggleBtn.addEventListener("click", () => {
      const current = (document.documentElement.getAttribute("data-theme") ?? "dark") as Theme;
      applyTheme(current === "dark" ? "light" : "dark");
      this.updateThemeIcon();
    });

    // ── Mute toggle ──
    this.muteToggleBtn = document.createElement("button");
    this.muteToggleBtn.className = "hud__mute-toggle";
    this.muteToggleBtn.setAttribute("aria-label", "Toggle sound");
    this.updateMuteIcon();
    this.muteToggleBtn.addEventListener("click", () => {
      toggleMute();
      this.updateMuteIcon();
    });

    const controlRight = document.createElement("div");
    controlRight.className = "hud__control-right";
    controlRight.appendChild(dailyBtn);
    controlRight.appendChild(campaignBtn);
    controlRight.appendChild(this.questsBtn);
    controlRight.appendChild(versusBtn);
    controlRight.appendChild(aftershockBtn);
    controlRight.appendChild(gauntletBtn);
    controlRight.appendChild(this.hintBtn);
    controlRight.appendChild(this.newGameBtn);
    controlRight.appendChild(modeLabel);
    controlRight.appendChild(this.themeToggleBtn);
    controlRight.appendChild(this.muteToggleBtn);

    controlRow.appendChild(controlLeft);
    controlRow.appendChild(controlRight);

    container.appendChild(diffRow);
    container.appendChild(statsRow);
    container.appendChild(controlRow);
  }

  // ─── Stat Block Builder ───────────────────────────────────────────────────────

  private createStatBlock(
    icon: string,
    initial: string,
    id: string,
    label: string
  ): HTMLElement {
    const block = document.createElement("div");
    block.className = "hud__stat";

    const topRow = document.createElement("div");
    topRow.className = "hud__stat-top";

    const iconEl = document.createElement("span");
    iconEl.className = "hud__stat-icon";
    iconEl.setAttribute("aria-hidden", "true");
    iconEl.textContent = icon;

    const valueEl = document.createElement("span");
    valueEl.className = "hud__stat-value";
    valueEl.id = id;
    valueEl.textContent = initial;

    topRow.appendChild(iconEl);
    topRow.appendChild(valueEl);

    const labelEl = document.createElement("span");
    labelEl.className = "hud__stat-label";
    labelEl.textContent = label;

    block.appendChild(topRow);
    block.appendChild(labelEl);
    return block;
  }

  // ─── Update Helpers ──────────────────────────────────────────────────────────

  private updateTimer(seconds: number, phase: string, limitSecs?: number): void {
    this.timerEl.classList.remove(
      "hud__stat-value--yellow",
      "hud__stat-value--red"
    );

    // Timed levels (6B) count DOWN and go red in the final stretch.
    if (limitSecs !== undefined) {
      const remaining = Math.max(0, limitSecs - seconds);
      this.timerEl.textContent = formatTime(remaining);
      if (remaining <= 10) {
        this.timerEl.classList.add("hud__stat-value--red");
        if (phase === "PLAY") this.pulseStat(this.timerEl);
      } else if (remaining <= 30) {
        this.timerEl.classList.add("hud__stat-value--yellow");
      }
      return;
    }

    this.timerEl.textContent = formatTime(seconds);
    if (seconds >= this.RED_THRESHOLD) {
      this.timerEl.classList.add("hud__stat-value--red");
    } else if (seconds >= this.YELLOW_THRESHOLD) {
      this.timerEl.classList.add("hud__stat-value--yellow");
    }

    if (phase === "PLAY" && seconds >= this.RED_THRESHOLD && seconds % 5 === 0) {
      this.pulseStat(this.timerEl);
    }
  }

  /**
   * Score display:
   * IDLE / PLAY → "—" (hidden to prevent flag info leak)
   * WIN / LOSE  → count up from 0 with amber flash reveal
   *
   * Resets cleanly on new game: tween cancelled, displayed value zeroed,
   * so there's no flash of stale numbers when a game restarts.
   */
  private updateScore(newTarget: number, phase: string): void {
    if (phase === "IDLE" || phase === "PLAY") {
      if (this.tweenHandle !== null) {
        cancelAnimationFrame(this.tweenHandle);
        this.tweenHandle = null;
      }
      // Only update DOM if it doesn't already show "—"
      // Prevents unnecessary reflow every second during play
      if (this.scoreEl.textContent !== "—") {
        this.scoreEl.textContent = "—";
        this.scoreEl.classList.remove("hud__stat-value--revealed");
      }
      this.displayedScore = 0;
      this.targetScore    = 0;
      return;
    }

    // WIN or LOSE: tween to final score
    if (newTarget === this.targetScore && this.scoreEl.textContent !== "—") return;

    this.scoreEl.classList.add("hud__stat-value--revealed");
    this.targetScore = newTarget;
    this.tweenStart  = performance.now();

    if (this.tweenHandle !== null) {
      cancelAnimationFrame(this.tweenHandle);
    }

    const animate = (timestamp: number): void => {
      const elapsed  = timestamp - this.tweenStart;
      const progress = Math.min(elapsed / this.tweenDuration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);
      this.displayedScore = Math.round(this.targetScore * eased);
      this.scoreEl.textContent = String(this.displayedScore);

      if (progress < 1) {
        this.tweenHandle = requestAnimationFrame(animate);
      } else {
        this.displayedScore      = this.targetScore;
        this.scoreEl.textContent = String(this.targetScore);
        this.tweenHandle         = null;
        this.pulseStat(this.scoreEl);
      }
    };

    this.tweenHandle = requestAnimationFrame(animate);
  }

  /**
   * Flags display: "placed/total" — e.g. "3/10"
   * Turns red if over-flagged (more flags than mines).
   */
  private updateMines(flagsPlaced: number, totalMines: number): void {
    this.minesEl.textContent = `${flagsPlaced}/${totalMines}`;
    this.minesEl.classList.toggle(
      "hud__stat-value--danger",
      flagsPlaced > totalMines
    );
  }

  /**
   * Status line: includes difficulty label for constant context.
   * "Easy · Tap any cell to start"
   * "Medium · Flag mines · Reveal safe cells"
   * "Expert · ✅ Board cleared!"
   */
  private updateStatus(phase: string, diffLabel: string): void {
    const messages: Record<string, string> = {
      IDLE: "Tap any cell to start",
      PLAY: "Flag mines · Reveal safe cells",
      WIN:  "✅ Board cleared!",
      LOSE: "💥 Mine detonated",
    };
    const msg = messages[phase] ?? "";
    // Show difficulty prefix on IDLE and PLAY only — WIN/LOSE are self-explanatory
    const showPrefix = phase === "IDLE" || phase === "PLAY";
    this.statusEl.textContent = showPrefix ? `${diffLabel} · ${msg}` : msg;
    this.statusEl.className = `hud__status hud__status--${phase.toLowerCase()}`;
  }

  private updateDiffButtons(activeId: string): void {
    this.diffButtons.forEach((btn, id) => {
      const isActive = id === activeId;
      btn.classList.toggle("hud__diff-btn--active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  private updateNewGameBtn(phase: string): void {
    const isEnd = phase === "WIN" || phase === "LOSE";
    this.newGameBtn.classList.toggle("hud__new-game-btn--prominent", isEnd);
  }

  private pulseStat(el: HTMLElement): void {
    el.classList.remove("hud__stat-value--pulse");
    void el.offsetWidth;
    el.classList.add("hud__stat-value--pulse");
    el.addEventListener(
      "animationend",
      () => el.classList.remove("hud__stat-value--pulse"),
      { once: true }
    );
  }

  private updateThemeIcon(): void {
    const isDark = document.documentElement.getAttribute("data-theme") !== "light";
    this.themeToggleBtn.innerHTML = isDark
      ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
           aria-hidden="true">
           <circle cx="12" cy="12" r="5"/>
           <line x1="12" y1="1" x2="12" y2="3"/>
           <line x1="12" y1="21" x2="12" y2="23"/>
           <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
           <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
           <line x1="1" y1="12" x2="3" y2="12"/>
           <line x1="21" y1="12" x2="23" y2="12"/>
           <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
           <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
         </svg>`
      : `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
           aria-hidden="true">
           <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
         </svg>`;
  }

  private updateMuteIcon(): void {
    const muted = isMuted();
    this.muteToggleBtn.title = muted ? "Unmute sounds" : "Mute sounds";
    this.muteToggleBtn.setAttribute("aria-label", muted ? "Unmute sounds" : "Mute sounds");
    this.muteToggleBtn.innerHTML = muted
      ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
           aria-hidden="true">
           <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
           <line x1="23" y1="9" x2="17" y2="15"/>
           <line x1="17" y1="9" x2="23" y2="15"/>
         </svg>`
      : `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
           aria-hidden="true">
           <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
           <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
           <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
         </svg>`;
  }
}